import { z } from 'zod';
import { inspectionPointSchema, IInspectionPoint } from './inspectionPoint';

/**
 * Schema for validating inspection point form data.
 * Extends the base inspection point schema with form-specific validations.
 *
 * @property {string} code - Inspection point code (max 5 characters)
 * @property {string} name - Inspection point name (max 255 characters)
 * @property {string} [bookingReferenceNumber] - Optional booking reference number
 */
export const inspectionPointFormSchema = inspectionPointSchema.extend({});

/**
 * Default form schema for new inspection points.
 * Makes all required fields optional with empty defaults.
 */
export const inspectionPointDefaultFormSchema = inspectionPointFormSchema.extend({
    code: z.string().max(5, 'Code can only be up to 5 characters long.').optional().default(''),
    name: z.string().max(255, 'Name can only be up to 255 characters long.').optional().default(''),
});

/**
 * TypeScript interface representing InspectionPoint form values.
 * Generated from the Zod schema for type safety.
 */
export type IInspectionPointFormValues = z.infer<typeof inspectionPointFormSchema>;

/**
 * Factory function to create form values for an inspection point.
 * Handles both new and existing inspection points.
 *
 * @param {IInspectionPoint} [inspectionPoint] - Optional existing inspection point to populate form
 * @returns {IInspectionPointFormValues} Form values with appropriate defaults
 */
export const InspectionPointFormValues = (inspectionPoint ?: IInspectionPoint) : IInspectionPointFormValues => {
    // Handle the default case (new inspection point)
    if (!inspectionPoint) {
        return inspectionPointDefaultFormSchema.parse({});
    }

    // Transform the IInspectionPoint data into IInspectionPointFormValues structure
    return inspectionPointFormSchema.parse({
        ...inspectionPoint,
    });
};

/**
 * Validates inspection point form values.
 * Logs any validation errors to the console.
 *
 * @param {IInspectionPointFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation passes, false otherwise
 */
export const validateInspectionPointForm = (formValues : IInspectionPointFormValues) : boolean => {
    try {
        inspectionPointFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Inspection point form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
